import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Bell, BellOff, Clock, X, Check, AlertCircle, Moon, Calendar, Sparkles, Flame } from 'lucide-react';
import { toast } from 'sonner';
import { getLocalDateString } from '../lib/api';

// Storage key for reminder settings
const REMINDER_STORAGE_KEY = 'bereket_reminder_settings';

// Bijeli dani (13, 14, 15 po Hijri kalendaru)
const getWhiteDays = () => {
  // Ova funkcija vraća datume bijelih dana za tekući mjesec
  // Bijeli dani su 13., 14. i 15. dan po Hijri kalendaru
  return [13, 14, 15];
};

// Dnevni savjeti za dobra djela
const dailyGoodDeeds = {
  hr: [
    'Nasmiješi se nekome danas - osmijeh je sadaka!',
    'Posjeti bolesnika ili nazovi nekoga koga voliš.',
    'Prouči Ajetul-Kursi nakon svakog namaza.',
    'Daj sadaku, makar i malo.',
    'Pomozi nekome bez da te pita.',
    'Oprosti nekome tko ti je nešto skrivio.',
    'Prouči SubhanAllah, Alhamdulillah, Allahu Akbar po 33 puta.',
    'Nazovi roditelje i provjeri kako su.',
    'Očisti nešto u džamiji ili javnom prostoru.',
    'Nahrani gladnu životinju.',
    'Podijeli znanje s nekim - uči nekoga nečemu korisnom.',
    'Klanjaj noćni namaz, makar dva rekata.',
    'Prouči dovu za ummeti-Muhammed.',
    'Pomiri dvojicu koji se svađaju.',
    'Reci lijepu riječ - i ona je sadaka!'
  ],
  en: [
    'Smile at someone today - a smile is charity!',
    'Visit someone sick or call someone you love.',
    'Recite Ayatul-Kursi after every prayer.',
    'Give charity, even if small.',
    'Help someone without being asked.',
    'Forgive someone who wronged you.',
    'Recite SubhanAllah, Alhamdulillah, Allahu Akbar 33 times each.',
    'Call your parents and check on them.',
    'Clean something in the mosque or public space.',
    'Feed a hungry animal.',
    'Share knowledge - teach someone something useful.',
    'Pray night prayer, even just two rakats.',
    'Make dua for the Ummah.',
    'Reconcile two people who are arguing.',
    'Say a kind word - it is also charity!'
  ]
};

export default function ReminderSettings({ isOpen, onClose }) {
  const { i18n } = useTranslation();
  const lang = i18n.language;
  
  const [settings, setSettings] = useState({
    enabled: false,
    time: '20:00',
    notificationsGranted: false,
    whiteDaysReminder: true,
    dailyGoodDeedReminder: true,
    dailyGoodDeedTime: '09:00',
    streakReminder: true,
    streakReminderTime: '21:00'
  });
  const [permissionStatus, setPermissionStatus] = useState('default');

  useEffect(() => {
    const saved = localStorage.getItem(REMINDER_STORAGE_KEY);
    if (saved) {
      try {
        const parsed = JSON.parse(saved);
        setSettings({
          enabled: parsed.enabled ?? false,
          time: parsed.time ?? '20:00',
          notificationsGranted: parsed.notificationsGranted ?? false,
          whiteDaysReminder: parsed.whiteDaysReminder ?? true,
          dailyGoodDeedReminder: parsed.dailyGoodDeedReminder ?? true,
          dailyGoodDeedTime: parsed.dailyGoodDeedTime ?? '09:00',
          streakReminder: parsed.streakReminder ?? true,
          streakReminderTime: parsed.streakReminderTime ?? '21:00'
        });
      } catch (e) {
        console.error('Error loading reminder settings:', e);
      }
    }
    
    if ('Notification' in window) {
      setPermissionStatus(Notification.permission);
    }
  }, []);

  const saveSettings = (newSettings) => {
    setSettings(newSettings);
    localStorage.setItem(REMINDER_STORAGE_KEY, JSON.stringify(newSettings));
    
    if (newSettings.notificationsGranted) {
      if (newSettings.enabled) scheduleEveningReminder(newSettings.time);
      else cancelReminder('evening');
      
      if (newSettings.dailyGoodDeedReminder) scheduleDailyGoodDeedReminder(newSettings.dailyGoodDeedTime);
      else cancelReminder('gooddeed');
      
      if (newSettings.whiteDaysReminder) scheduleWhiteDaysReminder();
      else cancelReminder('whitedays');
      
      if (newSettings.streakReminder) scheduleStreakReminder(newSettings.streakReminderTime);
      else cancelReminder('streak');
    }
  };

  const requestNotificationPermission = async () => {
    if (!('Notification' in window)) {
      toast.error(lang === 'hr' 
        ? 'Obavijesti nisu podržane u ovom pregledniku' 
        : 'Notifications not supported in this browser');
      return;
    }

    try {
      const permission = await Notification.requestPermission();
      setPermissionStatus(permission);
      
      if (permission === 'granted') {
        saveSettings({ ...settings, notificationsGranted: true, enabled: true });
        toast.success(lang === 'hr' 
          ? 'Obavijesti su omogućene!' 
          : 'Notifications enabled!');
        
        new Notification(lang === 'hr' ? 'Bereket Podsjetnik' : 'Barakah Reminder', {
          body: lang === 'hr' 
            ? 'Obavijesti su aktivirane!' 
            : 'Notifications activated!',
          icon: '/icon-192.png',
          tag: 'bereket-test'
        });
      } else {
        toast.error(lang === 'hr' 
          ? 'Dozvola za obavijesti je odbijena' 
          : 'Notification permission denied');
      }
    } catch (error) {
      console.error('Error requesting notification permission:', error);
    }
  };

  const scheduleEveningReminder = (time) => {
    cancelReminder('evening');
    const [hours, minutes] = time.split(':').map(Number);
    const now = new Date();
    let scheduledTime = new Date();
    scheduledTime.setHours(hours, minutes, 0, 0);
    
    if (scheduledTime <= now) {
      scheduledTime.setDate(scheduledTime.getDate() + 1);
    }
    
    const delay = scheduledTime.getTime() - now.getTime();
    
    const timeoutId = setTimeout(() => {
      showEveningNotification();
      scheduleEveningReminder(time);
    }, delay);
    
    window.bereketEveningTimeout = timeoutId;
  };

  const scheduleDailyGoodDeedReminder = (time) => {
    cancelReminder('gooddeed');
    const [hours, minutes] = time.split(':').map(Number);
    const now = new Date();
    let scheduledTime = new Date();
    scheduledTime.setHours(hours, minutes, 0, 0);
    
    if (scheduledTime <= now) {
      scheduledTime.setDate(scheduledTime.getDate() + 1);
    }
    
    const delay = scheduledTime.getTime() - now.getTime();
    
    const timeoutId = setTimeout(() => {
      showDailyGoodDeedNotification();
      scheduleDailyGoodDeedReminder(time);
    }, delay);
    
    window.bereketGoodDeedTimeout = timeoutId;
  };

  const scheduleWhiteDaysReminder = () => {
    cancelReminder('whitedays');
    
    // Check every day at 18:00 if tomorrow is a white day
    const now = new Date();
    let checkTime = new Date();
    checkTime.setHours(18, 0, 0, 0);
    
    if (checkTime <= now) {
      checkTime.setDate(checkTime.getDate() + 1);
    }
    
    const delay = checkTime.getTime() - now.getTime();
    
    const timeoutId = setTimeout(() => {
      checkAndNotifyWhiteDays();
      scheduleWhiteDaysReminder();
    }, delay);
    
    window.bereketWhiteDaysTimeout = timeoutId;
  };

  const scheduleStreakReminder = (time) => {
    cancelReminder('streak');
    const [hours, minutes] = time.split(':').map(Number);
    const now = new Date();
    let scheduledTime = new Date();
    scheduledTime.setHours(hours, minutes, 0, 0);
    
    if (scheduledTime <= now) {
      scheduledTime.setDate(scheduledTime.getDate() + 1);
    }
    
    const delay = scheduledTime.getTime() - now.getTime();
    
    const timeoutId = setTimeout(() => {
      showStreakNotification();
      scheduleStreakReminder(time);
    }, delay);
    
    window.bereketStreakTimeout = timeoutId;
  };

  const checkAndNotifyWhiteDays = async () => {
    try {
      // Fetch current Hijri date from Aladhan API
      const tomorrow = new Date();
      tomorrow.setDate(tomorrow.getDate() + 1);
      const requestDate = `${String(tomorrow.getDate()).padStart(2, '0')}-${String(tomorrow.getMonth() + 1).padStart(2, '0')}-${tomorrow.getFullYear()}`;
      const response = await fetch(`https://api.aladhan.com/v1/gToH/${requestDate}`);
      const data = await response.json();
      
      if (data.code === 200) {
        const tomorrowHijriDay = parseInt(data.data.hijri.day);
        const whiteDays = getWhiteDays();
        
        if (whiteDays.includes(tomorrowHijriDay)) {
          showWhiteDaysNotification(tomorrowHijriDay);
        }
      }
    } catch (error) {
      console.error('Error checking white days:', error);
    }
  };

  const showEveningNotification = () => {
    if (Notification.permission === 'granted') {
      new Notification(lang === 'hr' ? 'Bereket Podsjetnik' : 'Barakah Reminder', {
        body: lang === 'hr' 
          ? 'Vrijeme je za refleksiju! Označi svoje dobre aktivnosti i razmisli o danu.' 
          : 'Time for reflection! Mark your good deeds and reflect on your day.',
        icon: '/icon-192.png',
        tag: 'bereket-evening',
        requireInteraction: true
      });
    }
  };

  const showDailyGoodDeedNotification = () => {
    if (Notification.permission === 'granted') {
      const deeds = dailyGoodDeeds[lang] || dailyGoodDeeds.en;
      const randomDeed = deeds[Math.floor(Math.random() * deeds.length)];
      
      new Notification(lang === 'hr' ? 'Ideja za Dobro Djelo' : 'Good Deed Idea', {
        body: randomDeed,
        icon: '/icon-192.png',
        tag: 'bereket-gooddeed',
        requireInteraction: true
      });
    }
  };

  const showWhiteDaysNotification = (hijriDay) => {
    if (Notification.permission === 'granted') {
      new Notification(lang === 'hr' ? 'Bijeli Dani - Post' : 'White Days - Fasting', {
        body: lang === 'hr' 
          ? `Sutra je ${hijriDay}. dan po Hijri kalendaru - jedan od bijelih dana! Post u bijele dane donosi veliku nagradu.` 
          : `Tomorrow is the ${hijriDay}th of Hijri month - one of the white days! Fasting the white days brings great reward.`,
        icon: '/icon-192.png',
        tag: 'bereket-whitedays',
        requireInteraction: true
      });
    }
  };

  const showStreakNotification = () => {
    if (Notification.permission === 'granted') {
      new Notification(lang === 'hr' ? '🔥 Sačuvaj Svoj Niz!' : '🔥 Keep Your Streak!', {
        body: lang === 'hr' 
          ? 'Još nisi zabilježio aktivnosti danas. Ne prekidaj svoj niz - svaki dan se računa!' 
          : "You haven't logged activities today. Don't break your streak - every day counts!",
        icon: '/icon-192.png',
        tag: 'bereket-streak',
        requireInteraction: true
      });
    }
  };

  const cancelReminder = (type) => {
    if (type === 'evening' && window.bereketEveningTimeout) {
      clearTimeout(window.bereketEveningTimeout);
      window.bereketEveningTimeout = null;
    }
    if (type === 'gooddeed' && window.bereketGoodDeedTimeout) {
      clearTimeout(window.bereketGoodDeedTimeout);
      window.bereketGoodDeedTimeout = null;
    }
    if (type === 'whitedays' && window.bereketWhiteDaysTimeout) {
      clearTimeout(window.bereketWhiteDaysTimeout);
      window.bereketWhiteDaysTimeout = null;
    }
    if (type === 'streak' && window.bereketStreakTimeout) {
      clearTimeout(window.bereketStreakTimeout);
      window.bereketStreakTimeout = null;
    }
  };

  const handleToggle = (field) => {
    if (!settings.notificationsGranted && !settings[field]) {
      requestNotificationPermission();
    } else {
      const newValue = !settings[field];
      saveSettings({ ...settings, [field]: newValue });
      
      if (newValue) {
        toast.success(lang === 'hr' ? 'Podsjetnik aktiviran!' : 'Reminder activated!');
      } else {
        toast.info(lang === 'hr' ? 'Podsjetnik deaktiviran' : 'Reminder deactivated');
      }
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div 
        className="bg-white rounded-2xl w-full max-w-md max-h-[90vh] overflow-y-auto"
        onClick={e => e.stopPropagation()}
      >
        <div className="p-6 border-b border-stone-200">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-accent/10 rounded-xl flex items-center justify-center">
                <Bell className="w-5 h-5 text-accent" />
              </div>
              <h2 className="text-xl font-heading font-bold text-[#292524]">
                {lang === 'hr' ? 'Podsjetnici' : 'Reminders'}
              </h2>
            </div>
            <button onClick={onClose} className="p-2 hover:bg-stone-100 rounded-lg">
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        <div className="p-6 space-y-6">
          {/* Permission Warning */}
          {permissionStatus === 'denied' && (
            <div className="p-4 bg-red-50 border border-red-200 rounded-xl flex items-start gap-3">
              <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-red-700 text-sm font-medium">
                  {lang === 'hr' ? 'Obavijesti blokirane' : 'Notifications blocked'}
                </p>
                <p className="text-red-600 text-xs mt-1">
                  {lang === 'hr' 
                    ? 'Molimo omogućite obavijesti u postavkama preglednika.' 
                    : 'Please enable notifications in browser settings.'}
                </p>
              </div>
            </div>
          )}

          {/* Evening Reminder */}
          <div className="p-4 bg-stone-50 rounded-xl space-y-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <Moon className="w-5 h-5 text-indigo-500" />
                <div>
                  <p className="font-medium text-[#292524]">
                    {lang === 'hr' ? 'Večernji podsjetnik' : 'Evening Reminder'}
                  </p>
                  <p className="text-sm text-[#57534E]">
                    {lang === 'hr' 
                      ? 'Podsjeti me da označim aktivnosti' 
                      : 'Remind me to mark my activities'}
                  </p>
                </div>
              </div>
              <button
                onClick={() => handleToggle('enabled')}
                disabled={permissionStatus === 'denied'}
                className={`w-12 h-7 rounded-full transition-all relative ${
                  settings.enabled ? 'bg-accent' : 'bg-stone-300'
                } ${permissionStatus === 'denied' ? 'opacity-50 cursor-not-allowed' : ''}`}
              >
                <div className={`absolute top-1 w-5 h-5 bg-white rounded-full shadow transition-all ${
                  settings.enabled ? 'left-6' : 'left-1'
                }`} />
              </button>
            </div>
            
            {settings.enabled && (
              <div className="flex items-center gap-2">
                <Clock className="w-4 h-4 text-[#57534E]" />
                <input
                  type="time"
                  value={settings.time}
                  onChange={(e) => saveSettings({ ...settings, time: e.target.value })}
                  className="flex-1 px-3 py-2 border border-stone-200 rounded-lg text-[#292524]"
                />
              </div>
            )}
          </div>

          {/* Daily Good Deed Reminder */}
          <div className="p-4 bg-emerald-50 rounded-xl space-y-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <Sparkles className="w-5 h-5 text-emerald-500" />
                <div>
                  <p className="font-medium text-[#292524]">
                    {lang === 'hr' ? 'Dnevna ideja za dobro djelo' : 'Daily Good Deed Idea'}
                  </p>
                  <p className="text-sm text-[#57534E]">
                    {lang === 'hr' 
                      ? 'Dobij prijedlog što možeš učiniti' 
                      : 'Get a suggestion for a good deed'}
                  </p>
                </div>
              </div>
              <button
                onClick={() => handleToggle('dailyGoodDeedReminder')}
                disabled={permissionStatus === 'denied'}
                className={`w-12 h-7 rounded-full transition-all relative ${
                  settings.dailyGoodDeedReminder ? 'bg-emerald-500' : 'bg-stone-300'
                } ${permissionStatus === 'denied' ? 'opacity-50 cursor-not-allowed' : ''}`}
              >
                <div className={`absolute top-1 w-5 h-5 bg-white rounded-full shadow transition-all ${
                  settings.dailyGoodDeedReminder ? 'left-6' : 'left-1'
                }`} />
              </button>
            </div>
            
            {settings.dailyGoodDeedReminder && (
              <div className="flex items-center gap-2">
                <Clock className="w-4 h-4 text-[#57534E]" />
                <input
                  type="time"
                  value={settings.dailyGoodDeedTime}
                  onChange={(e) => saveSettings({ ...settings, dailyGoodDeedTime: e.target.value })}
                  className="flex-1 px-3 py-2 border border-emerald-200 rounded-lg text-[#292524] bg-white"
                />
              </div>
            )}
          </div>

          {/* White Days Reminder */}
          <div className="p-4 bg-amber-50 rounded-xl">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <Calendar className="w-5 h-5 text-amber-500" />
                <div>
                  <p className="font-medium text-[#292524]">
                    {lang === 'hr' ? 'Bijeli dani (post)' : 'White Days (fasting)'}
                  </p>
                  <p className="text-sm text-[#57534E]">
                    {lang === 'hr' 
                      ? 'Podsjeti me dan prije bijelih dana' 
                      : 'Remind me before white days'}
                  </p>
                </div>
              </div>
              <button
                onClick={() => handleToggle('whiteDaysReminder')}
                disabled={permissionStatus === 'denied'}
                className={`w-12 h-7 rounded-full transition-all relative ${
                  settings.whiteDaysReminder ? 'bg-amber-500' : 'bg-stone-300'
                } ${permissionStatus === 'denied' ? 'opacity-50 cursor-not-allowed' : ''}`}
              >
                <div className={`absolute top-1 w-5 h-5 bg-white rounded-full shadow transition-all ${
                  settings.whiteDaysReminder ? 'left-6' : 'left-1'
                }`} />
              </button>
            </div>
            <p className="text-xs text-amber-700 mt-3 bg-amber-100 p-2 rounded-lg">
              {lang === 'hr' 
                ? '13., 14. i 15. dan po Hijri kalendaru. Post u ove dane je sunnet.' 
                : '13th, 14th, and 15th of each Hijri month. Fasting these days is Sunnah.'}
            </p>
          </div>

          {/* Streak Reminder */}
          <div className="p-4 bg-orange-50 rounded-xl space-y-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <Flame className="w-5 h-5 text-orange-500" />
                <div>
                  <p className="font-medium text-[#292524]">
                    {lang === 'hr' ? 'Podsjetnik za niz' : 'Streak Reminder'}
                  </p>
                  <p className="text-sm text-[#57534E]">
                    {lang === 'hr' 
                      ? 'Podsjeti me da sačuvam svoj niz' 
                      : 'Remind me to keep my streak'}
                  </p>
                </div>
              </div>
              <button
                onClick={() => handleToggle('streakReminder')}
                disabled={permissionStatus === 'denied'}
                className={`w-12 h-7 rounded-full transition-all relative ${
                  settings.streakReminder ? 'bg-orange-500' : 'bg-stone-300'
                } ${permissionStatus === 'denied' ? 'opacity-50 cursor-not-allowed' : ''}`}
              >
                <div className={`absolute top-1 w-5 h-5 bg-white rounded-full shadow transition-all ${
                  settings.streakReminder ? 'left-6' : 'left-1'
                }`} />
              </button>
            </div>
            
            {settings.streakReminder && (
              <div className="flex items-center gap-2">
                <Clock className="w-4 h-4 text-[#57534E]" />
                <input
                  type="time"
                  value={settings.streakReminderTime}
                  onChange={(e) => saveSettings({ ...settings, streakReminderTime: e.target.value })}
                  className="flex-1 px-3 py-2 border border-orange-200 rounded-lg text-[#292524] bg-white"
                />
              </div>
            )}
          </div>

          {/* Info Box */}
          <div className="p-4 bg-stone-100 rounded-xl">
            <h3 className="font-semibold text-[#292524] mb-2 text-sm">
              {lang === 'hr' ? '💡 O podsjetnicima' : '💡 About Reminders'}
            </h3>
            <ul className="text-xs text-[#57534E] space-y-1">
              <li>• {lang === 'hr' 
                ? 'Večernji podsjetnik te podsjeća da zabilježiš dobra djela' 
                : 'Evening reminder helps you log your good deeds'}</li>
              <li>• {lang === 'hr' 
                ? 'Dnevna ideja ti daje inspiraciju za sadaku i dobro djelo' 
                : 'Daily idea gives you inspiration for charity and good deeds'}</li>
              <li>• {lang === 'hr' 
                ? 'Bijeli dani su sunnet post koji donosi veliku nagradu' 
                : 'White days are Sunnah fasting days with great reward'}</li>
              <li>• {lang === 'hr' 
                ? 'Niz podsjetnik te motivira da ne prekidaš svoju rutinu' 
                : 'Streak reminder motivates you not to break your routine'}</li>
            </ul>
          </div>

          {/* Active Reminders Status */}
          {(settings.enabled || settings.dailyGoodDeedReminder || settings.whiteDaysReminder || settings.streakReminder) && (
            <div className="flex flex-wrap gap-2 justify-center">
              {settings.enabled && (
                <span className="flex items-center gap-1 px-3 py-1 bg-indigo-100 text-indigo-700 rounded-full text-xs">
                  <Check className="w-3 h-3" />
                  {lang === 'hr' ? `Večernji u ${settings.time}` : `Evening at ${settings.time}`}
                </span>
              )}
              {settings.dailyGoodDeedReminder && (
                <span className="flex items-center gap-1 px-3 py-1 bg-emerald-100 text-emerald-700 rounded-full text-xs">
                  <Check className="w-3 h-3" />
                  {lang === 'hr' ? `Ideja u ${settings.dailyGoodDeedTime}` : `Idea at ${settings.dailyGoodDeedTime}`}
                </span>
              )}
              {settings.whiteDaysReminder && (
                <span className="flex items-center gap-1 px-3 py-1 bg-amber-100 text-amber-700 rounded-full text-xs">
                  <Check className="w-3 h-3" />
                  {lang === 'hr' ? 'Bijeli dani' : 'White days'}
                </span>
              )}
              {settings.streakReminder && (
                <span className="flex items-center gap-1 px-3 py-1 bg-orange-100 text-orange-700 rounded-full text-xs">
                  <Check className="w-3 h-3" />
                  {lang === 'hr' ? `Niz u ${settings.streakReminderTime}` : `Streak at ${settings.streakReminderTime}`}
                </span>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
